import { useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  PageHeader,
  Subtitle,
  VBadge,
  VButton,
  VDialog,
  VDialogContent,
  VField,
  VInput,
  VTextarea,
} from "@/components/ui";
import type { TaskCategory, TaskTemplate } from "@/domains/tasks";
import {
  TASK_CATEGORY_LABELS,
  getTaskTemplateIcon,
  useTaskTemplates,
} from "@/domains/tasks";

const CATEGORY_BADGE_COLOR: Record<
  TaskCategory,
  React.ComponentProps<typeof VBadge>["color"]
> = {
  placement: "info",
  operations: "primary",
  external: "warning",
  administration: "secondary",
  decommission: "success",
};
import {
  useCreateTaskTemplate,
  useDeleteTaskTemplate,
  useSaveTaskTemplate,
} from "@/domains/tasks/api/mutations";

type DialogState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; template: TaskTemplate };

export function TaskTemplatesPage(): React.JSX.Element {
  const { data: templates, isPending } = useTaskTemplates();
  const create = useCreateTaskTemplate();
  const save = useSaveTaskTemplate();
  const remove = useDeleteTaskTemplate();
  const [dialog, setDialog] = useState<DialogState>({ kind: "closed" });

  const handleDelete = (template: TaskTemplate): void => {
    const ok = window.confirm(`Template "${template.name}" verwijderen?`);
    if (!ok) return;
    remove.mutate(template.id);
  };

  return (
    <div className="space-y-6">
      <Link
        to="/configuration"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Terug naar configuratie
      </Link>

      <PageHeader
        title="Taken templates"
        subtitle="Standaard takenlijsten die in één klik worden toegepast op een dossier."
        actions={
          <VButton onClick={() => setDialog({ kind: "create" })}>
            <Plus className="size-4" aria-hidden />
            Nieuwe template
          </VButton>
        }
      />

      {isPending ? (
        <Subtitle>Templates laden…</Subtitle>
      ) : (templates ?? []).length === 0 ? (
        <EmptyState
          title="Geen taken templates"
          description="Maak een template aan met standaardtaken voor terugkerende dossiers."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {(templates ?? []).map((tpl) => {
            const Icon = getTaskTemplateIcon(tpl.id);
            return (
            <Card key={tpl.id} padding="lg" className="flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2">
                  <span className="mt-0.5 inline-flex size-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <CardTitle className="truncate">{tpl.name}</CardTitle>
                    <Caption tone="muted">{tpl.items.length} taken in deze template</Caption>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <VButton
                    variant="ghost"
                    size="icon"
                    aria-label="Bewerken"
                    onClick={() => setDialog({ kind: "edit", template: tpl })}
                    icon={<Pencil className="size-4" aria-hidden />}
                  />
                  <VButton
                    variant="ghost"
                    size="icon"
                    aria-label="Verwijderen"
                    onClick={() => handleDelete(tpl)}
                    icon={<Trash2 className="size-4 text-error-700" aria-hidden />}
                  />
                </div>
              </div>
              <Body tone="muted" className="text-sm">
                {tpl.description}
              </Body>
              <div className="flex flex-wrap gap-1">
                {Array.from(new Set(tpl.items.map((i) => i.category))).map((cat) => (
                  <VBadge key={cat} color={CATEGORY_BADGE_COLOR[cat]}>
                    {TASK_CATEGORY_LABELS[cat]}
                  </VBadge>
                ))}
              </div>
            </Card>
            );
          })}
        </div>
      )}

      {dialog.kind !== "closed" ? (
        <TaskTemplateDialog
          mode={dialog}
          onClose={() => setDialog({ kind: "closed" })}
          onCreate={(input) =>
            create.mutate(input, { onSuccess: () => setDialog({ kind: "closed" }) })
          }
          onSave={(template) =>
            save.mutate(template, { onSuccess: () => setDialog({ kind: "closed" }) })
          }
          isPending={create.isPending || save.isPending}
        />
      ) : null}
    </div>
  );
}

type TaskTemplateDialogProps = {
  mode: { kind: "create" } | { kind: "edit"; template: TaskTemplate };
  onClose: () => void;
  onCreate: (input: Omit<TaskTemplate, "id">) => void;
  onSave: (template: TaskTemplate) => void;
  isPending: boolean;
};

function TaskTemplateDialog({
  mode,
  onClose,
  onCreate,
  onSave,
  isPending,
}: TaskTemplateDialogProps): React.JSX.Element {
  const initial =
    mode.kind === "edit"
      ? { name: mode.template.name, description: mode.template.description }
      : { name: "", description: "" };
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);

  const handleSubmit = (): void => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) return;
    if (mode.kind === "create") {
      onCreate({ name: trimmedName, description: description.trim(), items: [] });
    } else {
      onSave({
        ...mode.template,
        name: trimmedName,
        description: description.trim(),
      });
    }
  };

  const title = mode.kind === "create" ? "Nieuwe taken template" : "Template bewerken";
  const confirm = mode.kind === "create" ? "Aanmaken" : "Opslaan";

  return (
    <VDialog open onOpenChange={(open) => !open && onClose()}>
      <VDialogContent
        title={title}
        description="Wijzig naam en beschrijving van de template. Item-CRUD is voorbehouden voor developers."
      >
        <div className="space-y-3">
          <VField label="Naam" htmlFor="tplName" required>
            <VInput
              id="tplName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </VField>
          <VField label="Beschrijving" htmlFor="tplDescription">
            <VTextarea
              id="tplDescription"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </VField>
          {mode.kind === "edit" ? (
            <div className="rounded-md bg-muted/40 px-3 py-3">
              <Caption tone="muted">Bevat {mode.template.items.length} taken:</Caption>
              <ul className="mt-1 space-y-0.5 text-xs">
                {mode.template.items.map((item, idx) => (
                  <li key={idx} className="flex justify-between gap-2 text-muted-foreground">
                    <span className="truncate">{item.title}</span>
                    <span className="shrink-0">{item.offsetDays >= 0 ? "+" : ""}{item.offsetDays}d</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <VButton variant="ghost" onClick={onClose} disabled={isPending}>
              Annuleren
            </VButton>
            <VButton onClick={handleSubmit} disabled={name.trim().length === 0 || isPending}>
              {isPending ? "Bezig…" : confirm}
            </VButton>
          </div>
        </div>
      </VDialogContent>
    </VDialog>
  );
}
